import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Edit3,
  FileSpreadsheet,
  FileText,
  Filter,
  History,
  Save,
  Search,
  Trash2,
  UploadCloud,
  ShoppingCart,
  X,
} from "lucide-react";
import settlementReportAPI from "../api/settlementReportAPI";
import "./Reports.css";

const TRACKING_COLUMNS = {
  fetchDate: "Fetch Date",
  trackingId: "Tracking ID",
  orderId: "Order ID",
  sellerName: "Seller Name",
};

const ORDER_COLUMNS = {
  dispatchDate: "Dispatch Date",
  orderId: "Order ID",
  orderItemStatus: "Order Item Status",
  skuId: "SKUID",
  fsn: "FSN",
};

const SETTLEMENT_COLUMNS = {
  orderId: "Order ID",
  reverseShipping: "Reverse Shipping Fee (Rs.)",
  dispatchDate: "Dispatch Date",
  skuId: "Seller SKU",
  bankSettlement: "Bank Settlement Value (Rs.)",
  itemReturnStatus: "Item Return Status",
  returnType: "Return Type",
};

const COLUMN_ALIASES = {
  fetchDate: ["fetch date", "fetch_date", "date"],
  trackingId: ["tracking id", "tracking_id", "trackingid", "awb", "awb number", "delivery tracking id"],
  orderId: ["order id", "order_id", "orderid"],
  sellerName: ["seller name", "seller_name", "seller"],
  dispatchDate: ["dispatch date", "dispatched date", "dispatched_date", "settlement dispatch date"],
  orderItemStatus: ["order item status", "order_item_status", "status"],
  skuId: ["skuid", "sku id", "sku", "seller sku"],
  fsn: ["fsn"],
  reverseShipping: ["reverse shipping fee (rs.)", "reverse shipping", "reverse_shipping"],
  bankSettlement: ["bank settlement value (rs.)", "bank settlement", "settlement value"],
  itemReturnStatus: ["item return status", "item_return_status"],
  returnType: ["return type", "return_type"],
};

const emptyUploadSummary = {
  fileName: "",
  totalRows: 0,
  imported: 0,
  duplicates: 0,
  linked: 0,
  errors: [],
};

const REPORTS_PAGE_SIZE = 20;
const REPORTS_INTERACTION_TRANSITION = "transform 160ms ease, box-shadow 160ms ease";

const todayDateKey = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const getCompactPageNumbers = (currentPage, pageCount) => {
  if (pageCount <= 8) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const firstPages = [1, 2, 3, 4, 5];
  const lastPages = [pageCount - 2, pageCount - 1, pageCount].filter((page) => page > 5);
  const pages = [...firstPages];
  const normalizedCurrentPage = Math.min(Math.max(Number(currentPage) || 1, 1), pageCount);

  if (normalizedCurrentPage > 6) pages.push("gap-left");
  if (normalizedCurrentPage > 5 && normalizedCurrentPage < pageCount - 2) pages.push(normalizedCurrentPage);
  if (normalizedCurrentPage < pageCount - 3) pages.push("gap-right");

  for (const page of lastPages) {
    if (!pages.includes(page)) pages.push(page);
  }

  return pages;
};

const normalize = (value) => String(value || "").trim();
const normalizeKey = (value) => normalize(value).toLowerCase();
const normalizeHeader = (value) => normalizeKey(value).replace(/[^a-z0-9]/g, "");

const toNumber = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.-]/g, "");
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatSettlementStatus = (status) =>
  String(status || "-") === "EXCESS_SETTLEMENT"
    ? "EXTRA PAYMENT"
    : String(status || "-").replace(/_/g, " ");

const readUploadFile = (file) =>
  new Promise((resolve, reject) => {
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(result.data || []),
        error: reject,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(event.target.result, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      resolve(XLSX.utils.sheet_to_json(sheet, { defval: "" }));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const validateColumns = (rows, mapping) => {
  const headers = new Set(Object.keys(rows[0] || {}).map(normalizeHeader));
  return Object.entries(mapping)
    .filter(([target]) => !(COLUMN_ALIASES[target] || [mapping[target]]).some((alias) => headers.has(normalizeHeader(alias))))
    .map(([, column]) => column);
};

const resolveValue = (row, sourceColumn) => {
  const foundKey = Object.keys(row).find((key) => normalize(key) === sourceColumn);
  return foundKey ? row[foundKey] : "";
};

const inferPlatform = (row) => {
  const joined = `${row.sku} ${row.fsn} ${row.orderId}`.toLowerCase();
  if (joined.includes("amazon")) return "AMAZON";
  if (joined.includes("meesho")) return "MEESHO";
  if (joined.includes("flipkart") || joined.includes("fk")) return "FLIPKART";
  return "FLIPKART";
};

const inferProductType = (sku) => {
  const value = normalize(sku);
  if (!value) return "Unmapped Product";
  return value.split(/[-_/ ]/).filter(Boolean).slice(0, 2).join(" ") || value;
};

const buildRuleStateCandidates = (report) => {
  const joined = [
    report.orderItemStatus,
    report.itemReturnStatus,
    report.returnType,
  ]
    .map(normalizeKey)
    .join(" ");

  const candidates = [];
  const add = (value) => {
    const normalized = normalizeKey(value);
    if (normalized && !candidates.includes(normalized)) candidates.push(normalized);
  };

  if (joined.includes("delivered")) add("delivered");
  if (joined.includes("rto")) add("rto");
  if (joined.includes("dto")) add("dto");
  if (joined.includes("customer") || joined.includes("return")) add("return");
  if (joined.includes("exchange")) add("exchange");

  add(report.orderItemStatus);
  add(report.itemReturnStatus);
  add(report.returnType);

  return candidates;
};

const classifySettlement = (expectedSettlement, bankSettlement, hasMatchingRule) => {
  if (!hasMatchingRule) {
    return { status: "PENDING_RULE", difference: 0, shortfall: 0, excess: 0 };
  }
  const difference = Number((bankSettlement - expectedSettlement).toFixed(2));
  if (difference === 0) return { status: "SETTLED", difference, shortfall: 0, excess: 0 };
  if (difference > 0) {
    return { status: "EXCESS_SETTLEMENT", difference, shortfall: 0, excess: difference };
  }
  return { status: "SHORT_SETTLEMENT", difference, shortfall: Math.abs(difference), excess: 0 };
};

const findSettlementRule = (rules, report) => {
  const platform = normalizeKey(report.platform);
  const productType = normalizeKey(report.productType);
  const sku = normalizeKey(report.sku);
  const stateCandidates = buildRuleStateCandidates(report);

  const matchedRules = rules
    .filter((rule) => {
    const rulePlatform = normalizeKey(rule.platform || "ALL");
    const productName = normalizeKey(rule.product_name);
      const ruleState = normalizeKey(rule.state);
    const platformMatches = rulePlatform === "all" || rulePlatform === platform;
    const productMatches = productName && (productName === productType || productName === sku);
      return platformMatches && productMatches && rule.status !== "Inactive";
    })
    .sort((a, b) => {
      const aPlatformScore = normalizeKey(a.platform || "ALL") === platform ? 4 : 0;
      const bPlatformScore = normalizeKey(b.platform || "ALL") === platform ? 4 : 0;
      const aProductScore = normalizeKey(a.product_name) === sku ? 3 : 1;
      const bProductScore = normalizeKey(b.product_name) === sku ? 3 : 1;
      const aStateScore = stateCandidates.includes(normalizeKey(a.state)) ? 2 : 0;
      const bStateScore = stateCandidates.includes(normalizeKey(b.state)) ? 2 : 0;
      return bPlatformScore + bProductScore + bStateScore - (aPlatformScore + aProductScore + aStateScore);
    });

  return matchedRules[0];
};

const applySettlementRules = (records, rules) =>
  records.map((record) => {
    const rule = findSettlementRule(rules, record);
    const expectedSettlement = Number(rule?.expected_settlement || 0);
    const bankSettlement = Number(record.bankSettlement || 0);
    const compared = record.hasSettlement
      ? classifySettlement(expectedSettlement, bankSettlement, Boolean(rule))
      : { status: "PENDING_SETTLEMENT", difference: 0, shortfall: 0, excess: 0 };

    return {
      ...record,
      expectedSettlement,
      settlementDifference: compared.difference,
      settlementStatus: compared.status,
      shortfallAmount: compared.shortfall,
      excessAmount: compared.excess,
      appliedSettlementRule: rule
        ? `${rule.product_name || record.productType} / ${rule.platform || "ALL"} / ${rule.state || "Default"}`
        : "No matching rule",
    };
  });

const mapServerRecord = (row) => ({
  id: row.id,
  orderId: row.order_id || "",
  orderItemStatus: row.order_item_status || "",
  sku: row.sku || "",
  fsn: row.fsn || "",
  awbNumber: row.tracking_id || "",
  fetchDate: row.tracking_fetch_date || "",
  sellerName: row.seller_name || "",
  dispatchDate: row.dispatch_date || "",
  quantity: Number(row.quantity || 1),
  uploadedAt: row.created_at || "",
  sourceFile: row.source_file || "",
  platform: row.platform || "FLIPKART",
  productType: row.product_type || "Unmapped Product",
  hasSettlement: Boolean(row.has_settlement),
  reverseShipping: Number(row.reverse_shipping || 0),
  settlementDispatchDate: row.settlement_dispatch_date || "",
  settlementSku: row.settlement_sku || "",
  bankSettlement: Number(row.bank_settlement || 0),
  itemReturnStatus: row.item_return_status || "",
  returnType: row.return_type || "",
  settlementSourceFile: row.settlement_source_file || "",
  expectedSettlement: Number(row.expected_settlement || 0),
  settlementDifference: Number(row.settlement_difference || 0),
  settlementStatus: row.settlement_status || "PENDING_SETTLEMENT",
  shortfallAmount: Number(row.shortfall_amount || 0),
  excessAmount: Number(row.excess_amount || 0),
  appliedSettlementRule: row.applied_settlement_rule || "No matching rule",
});

const buildRecordEditForm = (row) => ({
  orderId: row.orderId || "",
  dispatchDate: row.dispatchDate || "",
  orderItemStatus: row.orderItemStatus || "",
  sku: row.sku || "",
  fsn: row.fsn || "",
  trackingId: row.awbNumber || "",
  fetchDate: row.fetchDate || "",
  sellerName: row.sellerName || "",
  platform: row.platform || "FLIPKART",
  productType: row.productType || "",
  returnType: row.returnType || "",
  itemReturnStatus: row.itemReturnStatus || "",
  reverseShipping: String(row.reverseShipping || 0),
  settlementDispatchDate: row.settlementDispatchDate || "",
  settlementSku: row.settlementSku || row.sku || "",
  bankSettlement: String(row.bankSettlement || 0),
  hasSettlement: Boolean(row.hasSettlement),
});

const mapUploadSummary = (fileName, result) => ({
  fileName,
  totalRows: result?.totalRows || 0,
  imported: result?.imported || 0,
  duplicates: result?.duplicates || 0,
  linked: result?.linked || 0,
  errors: [],
});

const mapUploadError = (fileName, message) => ({
  ...emptyUploadSummary,
  fileName,
  errors: [message || "Upload failed"],
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN");
};

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [sellerNames, setSellerNames] = useState([]);
  const [summaries, setSummaries] = useState({
    tracking: emptyUploadSummary,
    primary: emptyUploadSummary,
    secondary: emptyUploadSummary,
  });
  const [uploadHistory, setUploadHistory] = useState([]);
  const [skuManagement, setSkuManagement] = useState({ inventory: [], mappings: [], unknownSkus: [] });
  const [showSkuManagement, setShowSkuManagement] = useState(false);
  const [skuSelections, setSkuSelections] = useState({});
  const [skuModalFilter, setSkuModalFilter] = useState("");
  const [manualSkuText, setManualSkuText] = useState("");
  const [manualProductId, setManualProductId] = useState("");
  const [skuBusy, setSkuBusy] = useState(false);
  const [showDifference, setShowDifference] = useState(false);
  const [differenceDate, setDifferenceDate] = useState(todayDateKey());
  const [differenceData, setDifferenceData] = useState(null);
  const [differenceBusy, setDifferenceBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [statusHistoryType, setStatusHistoryType] = useState("ALL");
  const [statusHistoryRows, setStatusHistoryRows] = useState([]);
  const [statusHistoryBusy, setStatusHistoryBusy] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [dummyMenuOpen, setDummyMenuOpen] = useState(false);
  const [uploadWorkflow, setUploadWorkflow] = useState(null);
  const [editingUploadId, setEditingUploadId] = useState(null);
  const [editingFileName, setEditingFileName] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordForm, setRecordForm] = useState(null);
  const [recordBusy, setRecordBusy] = useState(false);
  const [reportsPage, setReportsPage] = useState(1);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [filters, setFilters] = useState({
    orderId: "",
    sku: "",
    awb: "",
    platform: "all",
    account: "all",
    productType: "all",
    dispatchDateFrom: "",
    dispatchDateTo: "",
    returnType: "all",
    itemReturnStatus: "all",
    settlementStatus: "all",
  });

  const fetchReportData = async () => {
    try {
      const [recordRows, uploadRows, sellerRows] = await Promise.all([
        settlementReportAPI.getRecords(),
        settlementReportAPI.getUploads(),
        settlementReportAPI.getSellerNames(),
      ]);
      setRecords(Array.isArray(recordRows) ? recordRows.map(mapServerRecord) : []);
      setUploadHistory(Array.isArray(uploadRows) ? uploadRows : []);
      setSellerNames(Array.isArray(sellerRows) ? sellerRows : []);
    } catch (error) {
      console.error("Failed to load settlement report data:", error);
      setRecords([]);
      setUploadHistory([]);
      setSellerNames([]);
    }
  };

  const loadStatusHistory = async (reportType = statusHistoryType) => {
    try {
      setStatusHistoryBusy(true);
      const rows = await settlementReportAPI.getStatusHistory(reportType);
      setStatusHistoryRows(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error("Failed to load status history:", error);
      setStatusHistoryRows([]);
    } finally {
      setStatusHistoryBusy(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const loadSkuManagement = async () => {
    setSkuBusy(true);
    try {
      const data = await settlementReportAPI.getSkuManagement();
      setSkuManagement({
        inventory: Array.isArray(data?.inventory) ? data.inventory : [],
        mappings: Array.isArray(data?.mappings) ? data.mappings : [],
        unknownSkus: Array.isArray(data?.unknownSkus) ? data.unknownSkus : [],
      });
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to load SKU management");
    } finally {
      setSkuBusy(false);
    }
  };

  const openSkuManagement = async () => {
    setSkuModalFilter("");
    setShowSkuManagement(true);
    await loadSkuManagement();
  };

  const assignSkuToProduct = async (sku) => {
    const inventoryId = Number(skuSelections[sku] || 0);
    if (!sku || !inventoryId) return;
    try {
      setSkuBusy(true);
      await settlementReportAPI.assignSkus({ inventoryId, skus: [sku] });
      setSkuSelections((prev) => ({ ...prev, [sku]: "" }));
      await Promise.all([loadSkuManagement(), fetchReportData()]);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to assign SKU");
    } finally {
      setSkuBusy(false);
    }
  };

  const assignManualSkus = async () => {
    const inventoryId = Number(manualProductId || 0);
    const skus = manualSkuText
      .split(/[\n,]+/)
      .map((sku) => sku.trim())
      .filter(Boolean);
    if (!inventoryId || !skus.length) return;

    try {
      setSkuBusy(true);
      await settlementReportAPI.assignSkus({ inventoryId, skus });
      setManualSkuText("");
      setManualProductId("");
      await Promise.all([loadSkuManagement(), fetchReportData()]);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to assign SKUs");
    } finally {
      setSkuBusy(false);
    }
  };

  const deleteSkuMapping = async (mapping) => {
    if (!window.confirm(`Remove SKU ${mapping.sku || mapping.id} mapping?`)) return;
    try {
      setSkuBusy(true);
      await settlementReportAPI.deleteSkuMapping(mapping.id);
      await Promise.all([loadSkuManagement(), fetchReportData()]);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to remove SKU mapping");
    } finally {
      setSkuBusy(false);
    }
  };

  const loadDifference = async (date = differenceDate) => {
    setDifferenceBusy(true);
    try {
      const data = await settlementReportAPI.getDifference(date);
      setDifferenceData(data || null);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to load difference report");
    } finally {
      setDifferenceBusy(false);
    }
  };

  const openDifference = async () => {
    setShowDifference(true);
    await loadDifference(differenceDate);
  };

  const reportTypes = {
    tracking: { title: "Tracking Seller Report", icon: UploadCloud, mapping: TRACKING_COLUMNS, summaryKey: "tracking", api: settlementReportAPI.uploadTracking },
    primary: { title: "Order Detail Report", icon: ShoppingCart, mapping: ORDER_COLUMNS, summaryKey: "primary", api: settlementReportAPI.uploadPrimary },
    secondary: { title: "Settlement Report", icon: FileText, mapping: SETTLEMENT_COLUMNS, summaryKey: "secondary", api: settlementReportAPI.uploadSecondary },
  };

  const beginUpload = (type) => {
    setUploadMenuOpen(false);
    setUploadWorkflow({ type, step: 1, file: null, rows: [], error: "", importing: false, result: null });
  };

  const selectUploadFile = async (file) => {
    if (!file || !uploadWorkflow) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!file.size || !["csv", "xlsx", "xls"].includes(extension)) {
      setUploadWorkflow((current) => ({ ...current, file: null, rows: [], error: !file.size ? "The selected file is empty." : "Choose a CSV or Excel file (.csv, .xlsx, .xls)." }));
      return;
    }
    try {
      const rows = await readUploadFile(file);
      if (!rows.length) throw new Error("No data rows were found in this file.");
      const { mapping } = reportTypes[uploadWorkflow.type];
      const missing = validateColumns(rows, mapping);
      if (missing.length) throw new Error(`Missing required columns: ${missing.join(", ")}`);
      setUploadWorkflow((current) => ({ ...current, file, rows, error: "" }));
    } catch (error) {
      setUploadWorkflow((current) => ({ ...current, file: null, rows: [], error: error.message || "The file could not be read." }));
    }
  };

  const confirmUpload = async () => {
    if (!uploadWorkflow?.file || uploadWorkflow.importing) return;
    const workflow = uploadWorkflow;
    const report = reportTypes[workflow.type];
    setUploadWorkflow((current) => ({ ...current, importing: true, error: "" }));
    setUploading(workflow.type);
    try {
      const result = await report.api({ fileName: workflow.file.name, rows: workflow.rows });
      setSummaries((prev) => ({ ...prev, [report.summaryKey]: mapUploadSummary(workflow.file.name, result) }));
      setUploadWorkflow((current) => ({ ...current, step: 4, importing: false, result }));
      await fetchReportData();
    } catch (error) {
      const data = error?.response?.data;
      setUploadWorkflow((current) => ({ ...current, importing: false, error: data?.message || error.message || "Import failed." }));
    } finally {
      setUploading("");
    }
  };

  const options = useMemo(() => {
    const unique = (key) => [...new Set(records.map((row) => row[key]).filter(Boolean))];
    const sellerOptions = [...new Set([
      ...sellerNames.map((item) => item?.name).filter(Boolean),
      ...unique("sellerName"),
    ])].sort((a, b) => a.localeCompare(b));
    return {
      platform: unique("platform"),
      account: sellerOptions,
      productType: unique("productType"),
      returnType: unique("returnType"),
      itemReturnStatus: unique("itemReturnStatus"),
      settlementStatus: unique("settlementStatus"),
    };
  }, [records, sellerNames]);

  // Dashboard counts keep their full values; card clicks only filter the comparison table.
  const dashboardRecords = useMemo(() => {
    return records.filter((record) => {
      if (filters.orderId && !normalizeKey(record.orderId).includes(normalizeKey(filters.orderId))) return false;
      if (filters.sku && !normalizeKey(record.sku).includes(normalizeKey(filters.sku))) return false;
      if (filters.awb && !normalizeKey(record.awbNumber).includes(normalizeKey(filters.awb))) return false;
      if (filters.platform !== "all" && record.platform !== filters.platform) return false;
      if (filters.account !== "all" && record.sellerName !== filters.account) return false;
      if (filters.productType !== "all" && record.productType !== filters.productType) return false;
      if (filters.dispatchDateFrom && (!record.dispatchDate || record.dispatchDate < filters.dispatchDateFrom)) return false;
      if (filters.dispatchDateTo && (!record.dispatchDate || record.dispatchDate > filters.dispatchDateTo)) return false;
      if (filters.returnType !== "all" && record.returnType !== filters.returnType) return false;
      if (filters.itemReturnStatus !== "all" && record.itemReturnStatus !== filters.itemReturnStatus) return false;
      return true;
    });
  }, [records, filters]);

  const filteredRecords = useMemo(
    () =>
      filters.settlementStatus === "all"
        ? dashboardRecords
        : dashboardRecords.filter((record) => record.settlementStatus === filters.settlementStatus),
    [dashboardRecords, filters.settlementStatus]
  );

  const filteredUnknownSkus = useMemo(() => {
    const query = normalizeKey(skuModalFilter);
    return skuManagement.unknownSkus.filter((item) => {
      if (!query) return true;
      return normalizeKey(`${item.sku || ""} ${item.record_count || ""}`).includes(query);
    });
  }, [skuManagement.unknownSkus, skuModalFilter]);

  const filteredSkuMappings = useMemo(() => {
    const query = normalizeKey(skuModalFilter);
    return skuManagement.mappings.filter((mapping) => {
      if (!query) return true;
      return normalizeKey(`${mapping.sku || ""} ${mapping.product_name || ""} ${mapping.inventory_id || ""}`).includes(query);
    });
  }, [skuManagement.mappings, skuModalFilter]);

  useEffect(() => {
    setReportsPage(1);
  }, [filters, records.length]);

  const reportsPageCount = Math.max(1, Math.ceil(filteredRecords.length / REPORTS_PAGE_SIZE));
  const safeReportsPage = Math.min(reportsPage, reportsPageCount);
  const paginatedRecords = useMemo(() => {
    const start = (safeReportsPage - 1) * REPORTS_PAGE_SIZE;
    return filteredRecords.slice(start, start + REPORTS_PAGE_SIZE);
  }, [filteredRecords, safeReportsPage]);
  const reportsStartIndex = filteredRecords.length ? (safeReportsPage - 1) * REPORTS_PAGE_SIZE + 1 : 0;
  const reportsEndIndex = Math.min(safeReportsPage * REPORTS_PAGE_SIZE, filteredRecords.length);

  const dashboard = useMemo(() => {
    const totalSettlementAmount = dashboardRecords.reduce((sum, row) => sum + Number(row.bankSettlement || 0), 0);
    const totalSettlementDifference = dashboardRecords.reduce(
      (sum, row) => sum + Number(row.settlementDifference || 0),
      0
    );
    const totalSettlementLoss = dashboardRecords.reduce((sum, row) => sum + Number(row.shortfallAmount || 0), 0);
    const totalExcessSettlementAmount = dashboardRecords.reduce((sum, row) => sum + Number(row.excessAmount || 0), 0);

    return [
      { label: "Total Orders", hint: "All filtered records", value: dashboardRecords.length, filterStatus: "all" },
      {
        label: "Awaiting Settlement",
        hint: "Bank settlement not received",
        value: dashboardRecords.filter((row) => row.settlementStatus === "PENDING_SETTLEMENT").length,
        tone: "pending",
        filterStatus: "PENDING_SETTLEMENT",
      },
      {
        label: "Rate Rule Needed",
        hint: "Bank value received, but no matching rule",
        value: dashboardRecords.filter((row) => row.settlementStatus === "PENDING_RULE").length,
        tone: "rule",
        filterStatus: "PENDING_RULE",
      },
      {
        label: "Matched Settlements",
        hint: "Expected amount equals bank amount",
        value: dashboardRecords.filter((row) => row.settlementStatus === "SETTLED").length,
        tone: "settled",
        filterStatus: "SETTLED",
      },
      {
        label: "Short Payment",
        hint: "Bank amount is lower than expected",
        value: dashboardRecords.filter((row) => row.settlementStatus === "SHORT_SETTLEMENT").length,
        tone: "loss",
        filterStatus: "SHORT_SETTLEMENT",
      },
      {
        label: "Extra Payment",
        hint: "Bank amount is higher than expected",
        value: dashboardRecords.filter((row) => row.settlementStatus === "EXCESS_SETTLEMENT").length,
        tone: "excess",
        filterStatus: "EXCESS_SETTLEMENT",
      },
      { label: "Bank Amount Received", hint: "Total bank settlement in current view", value: formatCurrency(totalSettlementAmount), tone: "money", filterStatus: "all" },
      { label: "Net Difference", hint: "Extra payment minus short payment", value: formatCurrency(totalSettlementDifference), tone: "diff", filterStatus: "all" },
      { label: "Short Amount", hint: "Total amount to investigate", value: formatCurrency(totalSettlementLoss), tone: "loss", filterStatus: "SHORT_SETTLEMENT" },
      { label: "Extra Amount", hint: "Total amount received above expected", value: formatCurrency(totalExcessSettlementAmount), tone: "excess", filterStatus: "EXCESS_SETTLEMENT" },
    ];
  }, [dashboardRecords]);

  const exportRows = () =>
    filteredRecords.map((row) => ({
      "Order ID": row.orderId,
      SKU: row.sku,
      "Tracking ID": row.awbNumber,
      "Fetch Date": row.fetchDate,
      "Seller Name": row.sellerName,
      Platform: row.platform,
      "Product Type": row.productType,
      "Order Status": row.orderItemStatus,
      "Dispatch Date": row.dispatchDate,
      Quantity: row.quantity,
      "Expected Settlement": row.expectedSettlement,
      "Bank Settlement": row.bankSettlement || 0,
      Difference: row.settlementDifference,
      Status: row.settlementStatus,
      "Applied Rule": row.appliedSettlementRule,
      "Return Type": row.returnType || "",
      "Item Return Status": row.itemReturnStatus || "",
      "Reverse Shipping": row.reverseShipping || 0,
      "Settlement Dispatch Date": row.settlementDispatchDate || "",
    }));

  const handleExportCsv = () => {
    const csv = Papa.unparse(exportRows());
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reports-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(exportRows());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
    XLSX.writeFile(workbook, `reports-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const downloadDummyReport = (fileName, columns) => {
    const headers = Object.values(columns);
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    worksheet["!cols"] = headers.map((header) => ({ wch: Math.max(String(header).length + 4, 16) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dummy Report");
    XLSX.writeFile(workbook, fileName);
  };

  const resetFilters = () => {
    setFilters({
      orderId: "",
      sku: "",
      awb: "",
      platform: "all",
      account: "all",
      productType: "all",
      dispatchDateFrom: "",
      dispatchDateTo: "",
      returnType: "all",
      itemReturnStatus: "all",
      settlementStatus: "all",
    });
  };

  const filterFromCard = (status = "all") => {
    setFilters((prev) => ({ ...prev, settlementStatus: status }));
    document.querySelector(".reports-table-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const startEditUpload = (upload) => {
    setEditingUploadId(upload.id);
    setEditingFileName(upload.file_name || "");
  };

  const saveUploadEdit = async () => {
    if (!editingUploadId || !editingFileName.trim()) return;
    try {
      setHistoryBusy(true);
      await settlementReportAPI.updateUpload(editingUploadId, { fileName: editingFileName.trim() });
      setEditingUploadId(null);
      setEditingFileName("");
      await fetchReportData();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to update history");
    } finally {
      setHistoryBusy(false);
    }
  };

  const deleteUpload = async (upload) => {
    if (!window.confirm(`Delete ${upload.file_name || "this upload"}?`)) return;
    try {
      setHistoryBusy(true);
      await settlementReportAPI.deleteUpload(upload.id);
      await fetchReportData();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to delete history");
    } finally {
      setHistoryBusy(false);
    }
  };

  const startEditRecord = (record) => {
    setEditingRecord(record);
    setRecordForm(buildRecordEditForm(record));
  };

  const updateRecordForm = (key, value) => {
    setRecordForm((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const saveRecordEdit = async () => {
    if (!editingRecord || !recordForm?.orderId?.trim()) return;
    try {
      setRecordBusy(true);
      await settlementReportAPI.updateRecord(editingRecord.id, recordForm);
      setEditingRecord(null);
      setRecordForm(null);
      await fetchReportData();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to update report record");
    } finally {
      setRecordBusy(false);
    }
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Delete report record ${record.orderId || record.id}?`)) return;
    try {
      setRecordBusy(true);
      await settlementReportAPI.deleteRecord(record.id);
      await fetchReportData();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to delete report record");
    } finally {
      setRecordBusy(false);
    }
  };

  return (
    <div className="reports-dashboard page-container">
      <header className="reports-hero">
        <div>
          <span className="reports-eyebrow">Report Workspace</span>
          <h1>Settlement Reports</h1>
          <p>Tracking, order detail aur settlement reports upload karke settlement mismatch track karein.</p>
        </div>
        <div className="reports-actions">
          <div className="reports-upload-menu-wrap">
            <button type="button" className="reports-btn secondary" onClick={() => setDummyMenuOpen((open) => !open)} aria-expanded={dummyMenuOpen}>
              <Download size={16} /> Download Dummy Report <ChevronDown size={15} />
            </button>
            {dummyMenuOpen && <div className="reports-upload-menu">
              {Object.entries(reportTypes).map(([type, report], index) => {
                const Icon = report.icon;
                return <button type="button" key={type} onClick={() => {
                  setDummyMenuOpen(false);
                  const fileNames = {
                    tracking: "tracking-seller-dummy-report.xlsx",
                    primary: "order-detail-dummy-report.xlsx",
                    secondary: "settlement-dummy-report.xlsx",
                  };
                  downloadDummyReport(fileNames[type], report.mapping);
                }}><Icon size={17} /><span>{index + 1}. {report.title}</span></button>;
              })}
            </div>}
          </div>
          <div className="reports-upload-menu-wrap">
            <button type="button" className="reports-btn primary" onClick={() => setUploadMenuOpen((open) => !open)} aria-expanded={uploadMenuOpen}>
              <UploadCloud size={16} /> Upload Report <ChevronDown size={15} />
            </button>
            {uploadMenuOpen && <div className="reports-upload-menu">
              {Object.entries(reportTypes).map(([type, report], index) => {
                const Icon = report.icon;
                return <button type="button" key={type} onClick={() => beginUpload(type)}><Icon size={17} /><span>{index + 1}. {report.title}</span></button>;
              })}
            </div>}
          </div>
          <button
            type="button"
            className="reports-btn secondary"
            onClick={() => {
              setShowStatusHistory(true);
              loadStatusHistory();
            }}
          >
            <History size={16} />
            Status History
          </button>
          <button type="button" className="reports-btn secondary" onClick={openSkuManagement}>
            <FileSpreadsheet size={16} />
            SKU Management
          </button>
          <button type="button" className="reports-btn secondary" onClick={() => navigate("/reports/difference")}>
            <Filter size={16} />
            Difference
          </button>
          <button type="button" className="reports-btn secondary" onClick={() => setShowHistory(true)}>
            <History size={16} />
            History
          </button>
          <button type="button" className="reports-btn secondary" onClick={handleExportCsv} disabled={!filteredRecords.length}>
            <Download size={16} />
            CSV
          </button>
          <button type="button" className="reports-btn primary" onClick={handleExportExcel} disabled={!filteredRecords.length}>
            <FileSpreadsheet size={16} />
            Excel
          </button>
        </div>
      </header>

      <section className="reports-kpi-grid">
        {dashboard.map((item) => (
          <button
            key={item.label}
            type="button"
            className={`reports-kpi ${item.tone || ""}`}
            style={{ transition: REPORTS_INTERACTION_TRANSITION }}
            onClick={() => filterFromCard(item.filterStatus)}
            title={`Show ${item.label} in table`}
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small>{item.hint}</small>
          </button>
        ))}
      </section>

      <section className="reports-filter-card">
        <div className="reports-section-title">
          <Filter size={18} />
          <h2>Filters</h2>
        </div>
        <div className="reports-filters">
          <label>
            <span>Order ID</span>
            <div className="reports-input-icon">
              <Search size={15} />
              <input value={filters.orderId} onChange={(event) => setFilters((prev) => ({ ...prev, orderId: event.target.value }))} />
            </div>
          </label>
          <label>
            <span>SKU</span>
            <input value={filters.sku} onChange={(event) => setFilters((prev) => ({ ...prev, sku: event.target.value }))} />
          </label>
          <label>
            <span>Tracking ID</span>
            <input value={filters.awb} onChange={(event) => setFilters((prev) => ({ ...prev, awb: event.target.value }))} />
          </label>
          <SelectFilter label="Platform" value={filters.platform} options={options.platform} onChange={(value) => setFilters((prev) => ({ ...prev, platform: value }))} />
          <SelectFilter label="Account" value={filters.account} options={options.account} onChange={(value) => setFilters((prev) => ({ ...prev, account: value }))} />
          <SelectFilter label="Product Type" value={filters.productType} options={options.productType} onChange={(value) => setFilters((prev) => ({ ...prev, productType: value }))} />
          <label className="reports-date-range-filter">
            <span>Dispatch Date</span>
            <div className="reports-date-range">
              <input
                type="date"
                value={filters.dispatchDateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dispatchDateFrom: event.target.value }))}
              />
              <input
                type="date"
                value={filters.dispatchDateTo}
                min={filters.dispatchDateFrom || undefined}
                onChange={(event) => setFilters((prev) => ({ ...prev, dispatchDateTo: event.target.value }))}
              />
            </div>
          </label>
          <SelectFilter label="Return Type" value={filters.returnType} options={options.returnType} onChange={(value) => setFilters((prev) => ({ ...prev, returnType: value }))} />
          <SelectFilter label="Item Return Status" value={filters.itemReturnStatus} options={options.itemReturnStatus} onChange={(value) => setFilters((prev) => ({ ...prev, itemReturnStatus: value }))} />
          <SelectFilter label="Settlement Status" value={filters.settlementStatus} options={options.settlementStatus} onChange={(value) => setFilters((prev) => ({ ...prev, settlementStatus: value }))} />
        </div>
        <button type="button" className="reports-btn ghost" onClick={resetFilters}>
          Clear Filters
        </button>
      </section>

      <section className="reports-table-card">
        <div className="reports-table-head">
          <div>
            <h2>Settlement Comparison</h2>
            <p>
              {reportsStartIndex}-{reportsEndIndex} records shown from {filteredRecords.length} filtered, {records.length} total records.
            </p>
          </div>
          <span>{uploadHistory.length} uploads saved</span>
        </div>
        <div className="reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>SKU</th>
                <th>AWB / Tracking ID</th>
                <th>Seller</th>
                <th>Platform</th>
                <th>Order Status</th>
                <th>Report Upload Date</th>
                <th>Return Status</th>
                <th className="reports-money-cell">Expected</th>
                <th className="reports-money-cell">Bank Received</th>
                <th className="reports-money-cell">Difference</th>
                <th>Result</th>
                <th>Applied Rule</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="14" className="reports-empty">No report records found.</td>
                </tr>
              ) : (
                paginatedRecords.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.orderId || "-"}</strong>
                    </td>
                    <td>
                      {row.sku || "-"}
                    </td>
                    <td>
                      {row.awbNumber || "-"}
                    </td>
                    <td>
                      {row.sellerName || "-"}
                    </td>
                    <td>
                      {row.platform || "-"}
                    </td>
                    <td>{row.orderItemStatus || "-"}</td>
                    <td>{formatDateTime(row.uploadedAt)}</td>
                    <td>{[row.returnType, row.itemReturnStatus].filter(Boolean).join(" / ") || "-"}</td>
                    <td className="reports-money-cell">{formatCurrency(row.expectedSettlement)}</td>
                    <td className="reports-money-cell">{row.hasSettlement ? formatCurrency(row.bankSettlement) : "-"}</td>
                    <td className={`reports-money-cell ${row.settlementDifference < 0 ? "negative" : row.settlementDifference > 0 ? "positive" : ""}`}>
                      {formatCurrency(row.settlementDifference)}
                    </td>
                    <td>
                      <span className={`reports-status ${row.settlementStatus.toLowerCase()}`}>
                        {formatSettlementStatus(row.settlementStatus)}
                      </span>
                    </td>
                    <td className="reports-rule-cell">{row.appliedSettlementRule}</td>
                    <td>
                      <div className="reports-row-actions">
                        <button
                          type="button"
                          className="reports-icon-btn"
                          onClick={() => startEditRecord(row)}
                          disabled={recordBusy}
                          title="Edit"
                        >
                          <Edit3 size={15} />
                        </button>
                        <button
                          type="button"
                          className="reports-icon-btn danger"
                          onClick={() => deleteRecord(row)}
                          disabled={recordBusy}
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredRecords.length > REPORTS_PAGE_SIZE && (
          <div className="reports-pagination">
            <button
              type="button"
              className="reports-pagination-btn"
              disabled={safeReportsPage === 1}
              onClick={() => setReportsPage((page) => Math.max(1, page - 1))}
            >
              Prev
            </button>
            {getCompactPageNumbers(safeReportsPage, reportsPageCount).map((page) =>
              typeof page === "string" ? (
                <span key={`reports-pagination-${page}`} className="reports-pagination-btn disabled">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={`reports-pagination-btn ${safeReportsPage === page ? "active" : ""}`}
                  onClick={() => setReportsPage(page)}
                >
                  {page}
                </button>
              )
            )}
            <span className="reports-pagination-current">
              Page {safeReportsPage} of {reportsPageCount}
            </span>
            <button
              type="button"
              className="reports-pagination-btn"
              disabled={safeReportsPage === reportsPageCount}
              onClick={() => setReportsPage((page) => Math.min(reportsPageCount, page + 1))}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {showDifference && (
        <div className="reports-modal-overlay">
          <div className="reports-difference-modal">
            <div className="reports-history-head">
              <div>
                <h2>Difference</h2>
                <p>Sales dispatched AWB aur tracking report IDs ka date-wise match.</p>
              </div>
              <button type="button" className="reports-icon-btn" onClick={() => setShowDifference(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="reports-difference-body">
              <div className="reports-difference-toolbar">
                <label>
                  <span>Date</span>
                  <input
                    type="date"
                    value={differenceDate}
                    onChange={(event) => {
                      setDifferenceDate(event.target.value);
                      loadDifference(event.target.value);
                    }}
                  />
                </label>
                <button type="button" className="reports-btn secondary" onClick={() => loadDifference()} disabled={differenceBusy}>
                  {differenceBusy ? "Loading..." : "Refresh"}
                </button>
              </div>

              <div className="reports-difference-kpis">
                <article>
                  <span>Products Dispatched</span>
                  <strong>{differenceData?.salesProductCount || 0}</strong>
                </article>
                <article>
                  <span>Sales AWB</span>
                  <strong>{differenceData?.salesAwbCount || 0}</strong>
                </article>
                <article>
                  <span>Tracking IDs Given</span>
                  <strong>{differenceData?.trackingIdCount || 0}</strong>
                </article>
                <article className={Number(differenceData?.difference || 0) === 0 ? "ok" : "issue"}>
                  <span>Difference</span>
                  <strong>{Number(differenceData?.difference || 0)}</strong>
                </article>
              </div>

              <div className="reports-difference-grid">
                <section className="reports-difference-panel">
                  <div className="reports-sku-section-head">
                    <div>
                      <h3>Missing AWB</h3>
                      <p>Sales me dispatched hai, tracking report me nahi mila.</p>
                    </div>
                  </div>
                  <DifferenceList
                    rows={differenceData?.missingAwbs || []}
                    emptyText="No missing AWB."
                    renderRow={(row) => (
                      <>
                        <strong>{row.awb_number || "-"}</strong>
                        <small>{row.product_name || "-"} / Qty {row.quantity || 1}</small>
                      </>
                    )}
                  />
                </section>

                <section className="reports-difference-panel">
                  <div className="reports-sku-section-head">
                    <div>
                      <h3>Extra Tracking IDs</h3>
                      <p>Tracking report me hai, sales AWB me nahi mila.</p>
                    </div>
                  </div>
                  <DifferenceList
                    rows={differenceData?.extraTrackingIds || []}
                    emptyText="No extra tracking ID."
                    renderRow={(row) => (
                      <>
                        <strong>{row.tracking_id || "-"}</strong>
                        <small>{row.order_id || "-"} / {row.seller_name || "-"}</small>
                      </>
                    )}
                  />
                </section>
              </div>

              <section className="reports-difference-panel">
                <div className="reports-sku-section-head">
                  <div>
                    <h3>Dispatched Product List</h3>
                    <p>{differenceData?.salesRows?.length || 0} sales AWB rows.</p>
                  </div>
                </div>
                <DifferenceList
                  rows={differenceData?.salesRows || []}
                  emptyText="No dispatched sales found for this date."
                  renderRow={(row) => (
                    <>
                      <strong>{row.product_name || "-"}</strong>
                      <small>{row.awb_number || "-"} / Qty {row.quantity || 1}</small>
                    </>
                  )}
                />
              </section>
            </div>
          </div>
        </div>
      )}

      {showSkuManagement && (
        <div className="reports-modal-overlay">
          <div className="reports-sku-modal">
            <div className="reports-history-head">
              <div>
                <h2>SKU Management</h2>
                <p>Report SKU IDs ko inventory products ke saath map karein.</p>
              </div>
              <button type="button" className="reports-icon-btn" onClick={() => setShowSkuManagement(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="reports-sku-body">
              <div className="reports-sku-toolbar">
                <div className="reports-input-icon">
                  <Search size={16} />
                  <input
                    value={skuModalFilter}
                    onChange={(event) => setSkuModalFilter(event.target.value)}
                    placeholder="Search SKU or product..."
                  />
                </div>
                <button type="button" className="reports-btn secondary" onClick={loadSkuManagement} disabled={skuBusy}>
                  Refresh
                </button>
              </div>

              <section className="reports-sku-manual-card reports-sku-priority-card">
                <div className="reports-sku-section-head">
                  <div>
                    <h3>Assign Multiple SKUs</h3>
                    <p>Product select karke comma ya new line se multiple SKU IDs add karein.</p>
                  </div>
                </div>
                <div className="reports-sku-manual-form">
                  <ProductPicker
                    products={skuManagement.inventory}
                    value={manualProductId}
                    onChange={setManualProductId}
                    placeholder="Search or select product"
                  />
                  <textarea
                    value={manualSkuText}
                    onChange={(event) => setManualSkuText(event.target.value)}
                    placeholder="SKU-1, SKU-2"
                  />
                  <button
                    type="button"
                    className="reports-btn primary"
                    onClick={assignManualSkus}
                    disabled={skuBusy || !manualProductId || !manualSkuText.trim()}
                  >
                    Assign SKUs
                  </button>
                </div>
              </section>

              <section className="reports-sku-new-card">
                <div className="reports-sku-section-head">
                  <div>
                    <h3>New SKU IDs</h3>
                    <p>{filteredUnknownSkus.length} shown from {skuManagement.unknownSkus.length} unmapped SKU IDs.</p>
                  </div>
                </div>

                {filteredUnknownSkus.length === 0 ? (
                  <div className="reports-sku-empty">No new SKU IDs found.</div>
                ) : (
                  <div className="reports-sku-list">
                    {filteredUnknownSkus.map((item) => (
                      <div key={item.sku} className="reports-sku-row">
                        <div>
                          <strong>{item.sku}</strong>
                          <small>{item.record_count || 0} report rows</small>
                        </div>
                        <ProductPicker
                          products={skuManagement.inventory}
                          value={skuSelections[item.sku] || ""}
                          onChange={(productId) =>
                            setSkuSelections((prev) => ({ ...prev, [item.sku]: productId }))
                          }
                          placeholder="Search or select product"
                        />
                        <button
                          type="button"
                          className="reports-btn primary"
                          onClick={() => assignSkuToProduct(item.sku)}
                          disabled={skuBusy || !skuSelections[item.sku]}
                        >
                          Assign
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="reports-sku-mapped-card">
                <div className="reports-sku-section-head">
                  <div>
                    <h3>Mapped SKUs</h3>
                    <p>{filteredSkuMappings.length} shown from {skuManagement.mappings.length} SKU mappings saved.</p>
                  </div>
                </div>
                <div className="reports-sku-mapping-list">
                  {filteredSkuMappings.length === 0 ? (
                    <div className="reports-sku-empty">No SKU mappings yet.</div>
                  ) : (
                    filteredSkuMappings.map((mapping) => (
                      <div key={mapping.id} className="reports-sku-mapping-row">
                        <div>
                          <strong>{mapping.sku}</strong>
                          <small>{mapping.product_name || `Product #${mapping.inventory_id}`}</small>
                        </div>
                        <button
                          type="button"
                          className="reports-icon-btn danger"
                          onClick={() => deleteSkuMapping(mapping)}
                          disabled={skuBusy}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {editingRecord && recordForm && (
        <div className="reports-modal-overlay">
          <div className="reports-edit-modal">
            <div className="reports-history-head">
              <div>
                <h2>Edit Report Record</h2>
                <p>{editingRecord.orderId || `Record #${editingRecord.id}`}</p>
              </div>
              <button
                type="button"
                className="reports-icon-btn"
                onClick={() => {
                  setEditingRecord(null);
                  setRecordForm(null);
                }}
                disabled={recordBusy}
              >
                <X size={18} />
              </button>
            </div>
            <div className="reports-edit-body">
              <label>
                <span>Order ID</span>
                <input value={recordForm.orderId} onChange={(event) => updateRecordForm("orderId", event.target.value)} />
              </label>
              <label>
                <span>Dispatch Date</span>
                <input value={recordForm.dispatchDate} onChange={(event) => updateRecordForm("dispatchDate", event.target.value)} />
              </label>
              <label>
                <span>Order Status</span>
                <input value={recordForm.orderItemStatus} onChange={(event) => updateRecordForm("orderItemStatus", event.target.value)} />
              </label>
              <label>
                <span>SKU</span>
                <input value={recordForm.sku} onChange={(event) => updateRecordForm("sku", event.target.value)} />
              </label>
              <label>
                <span>FSN</span>
                <input value={recordForm.fsn} onChange={(event) => updateRecordForm("fsn", event.target.value)} />
              </label>
              <label>
                <span>Tracking ID</span>
                <input value={recordForm.trackingId} onChange={(event) => updateRecordForm("trackingId", event.target.value)} />
              </label>
              <label>
                <span>Fetch Date</span>
                <input value={recordForm.fetchDate} onChange={(event) => updateRecordForm("fetchDate", event.target.value)} />
              </label>
              <label>
                <span>Seller Name</span>
                <select value={recordForm.sellerName} onChange={(event) => updateRecordForm("sellerName", event.target.value)}>
                  <option value="">Select seller name</option>
                  {options.account.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
              <label>
                <span>Platform</span>
                <input value={recordForm.platform} onChange={(event) => updateRecordForm("platform", event.target.value)} />
              </label>
              <label>
                <span>Product Type</span>
                <input value={recordForm.productType} onChange={(event) => updateRecordForm("productType", event.target.value)} />
              </label>
              <label>
                <span>Return Type</span>
                <input value={recordForm.returnType} onChange={(event) => updateRecordForm("returnType", event.target.value)} />
              </label>
              <label>
                <span>Item Return Status</span>
                <input value={recordForm.itemReturnStatus} onChange={(event) => updateRecordForm("itemReturnStatus", event.target.value)} />
              </label>
              <label>
                <span>Reverse Shipping</span>
                <input value={recordForm.reverseShipping} onChange={(event) => updateRecordForm("reverseShipping", event.target.value)} />
              </label>
              <label>
                <span>Settlement Dispatch Date</span>
                <input value={recordForm.settlementDispatchDate} onChange={(event) => updateRecordForm("settlementDispatchDate", event.target.value)} />
              </label>
              <label>
                <span>Settlement SKU</span>
                <input value={recordForm.settlementSku} onChange={(event) => updateRecordForm("settlementSku", event.target.value)} />
              </label>
              <label>
                <span>Bank Settlement</span>
                <input value={recordForm.bankSettlement} onChange={(event) => updateRecordForm("bankSettlement", event.target.value)} />
              </label>
              <label className="reports-edit-check">
                <input
                  type="checkbox"
                  checked={recordForm.hasSettlement}
                  onChange={(event) => updateRecordForm("hasSettlement", event.target.checked)}
                />
                <span>Has Settlement</span>
              </label>
            </div>
            <div className="reports-edit-actions">
              <button
                type="button"
                className="reports-btn secondary"
                onClick={() => {
                  setEditingRecord(null);
                  setRecordForm(null);
                }}
                disabled={recordBusy}
              >
                Cancel
              </button>
              <button type="button" className="reports-btn primary" onClick={saveRecordEdit} disabled={recordBusy}>
                <Save size={16} />
                {recordBusy ? "Saving..." : "Save Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="reports-modal-overlay">
          <div className="reports-history-modal">
            <div className="reports-history-head">
              <div>
                <h2>Upload History</h2>
                <p>Tracking, order detail aur settlement report uploads yahan manage honge.</p>
              </div>
              <button type="button" className="reports-icon-btn" onClick={() => setShowHistory(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="reports-history-wrap">
              <table className="reports-history-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>File</th>
                    <th>Rows</th>
                    <th>Imported</th>
                    <th>Duplicates</th>
                    <th>Linked</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadHistory.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="reports-empty">No uploads found.</td>
                    </tr>
                  ) : (
                    uploadHistory.map((upload) => (
                      <tr key={upload.id}>
                        <td>
                          <span className={`reports-upload-type ${String(upload.upload_type || "").toLowerCase()}`}>
                            {upload.upload_type}
                          </span>
                        </td>
                        <td>
                          {editingUploadId === upload.id ? (
                            <input
                              className="reports-history-input"
                              value={editingFileName}
                              onChange={(event) => setEditingFileName(event.target.value)}
                            />
                          ) : (
                            <strong>{upload.file_name || "-"}</strong>
                          )}
                          {upload.error_message && <small>{upload.error_message}</small>}
                        </td>
                        <td>{upload.total_rows || 0}</td>
                        <td>{upload.imported_rows || 0}</td>
                        <td>{upload.duplicate_rows || 0}</td>
                        <td>{upload.linked_rows || 0}</td>
                        <td>
                          <span className={`reports-history-status ${upload.error_message ? "failed" : "success"}`}>
                            {upload.error_message ? "Issue" : "Saved"}
                          </span>
                        </td>
                        <td>{formatDateTime(upload.created_at)}</td>
                        <td>
                          <div className="reports-history-actions">
                            {editingUploadId === upload.id ? (
                              <button type="button" className="reports-icon-btn" onClick={saveUploadEdit} disabled={historyBusy}>
                                <Save size={16} />
                              </button>
                            ) : (
                              <button type="button" className="reports-icon-btn" onClick={() => startEditUpload(upload)} disabled={historyBusy}>
                                <Edit3 size={16} />
                              </button>
                            )}
                            <button type="button" className="reports-icon-btn danger" onClick={() => deleteUpload(upload)} disabled={historyBusy}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showStatusHistory && (
        <div className="reports-modal-overlay">
          <div className="reports-history-modal">
            <div className="reports-history-head">
              <div>
                <h2>Status History</h2>
                <p>Order ka previous aur updated settlement status.</p>
              </div>
              <button type="button" className="reports-icon-btn" onClick={() => setShowStatusHistory(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="reports-history-filter">
              <label>
                <span>Report Type</span>
                <select
                  value={statusHistoryType}
                  onChange={(event) => {
                    const value = event.target.value;
                    setStatusHistoryType(value);
                    loadStatusHistory(value);
                  }}
                >
                  <option value="ALL">All 3 Reports</option>
                  <option value="TRACKING">Tracking Seller Report</option>
                  <option value="ORDER_DETAIL">Order Detail Report</option>
                  <option value="SETTLEMENT">Settlement Report</option>
                </select>
              </label>
            </div>
            <div className="reports-history-wrap">
              <table className="reports-history-table">
                <thead>
                  <tr>
                    <th>Changed On</th>
                    <th>Report</th>
                    <th>Order ID</th>
                    <th>Previous Status</th>
                    <th>Updated Status</th>
                    <th>Previous Difference</th>
                    <th>Updated Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {statusHistoryBusy ? (
                    <tr><td colSpan="7" className="reports-empty">Loading history...</td></tr>
                  ) : statusHistoryRows.length === 0 ? (
                    <tr><td colSpan="7" className="reports-empty">No status changes found.</td></tr>
                  ) : (
                    statusHistoryRows.map((entry) => (
                      <tr key={entry.id}>
                        <td>{formatDateTime(entry.changed_at)}</td>
                        <td>{String(entry.report_type || "").replace(/_/g, " ")}</td>
                        <td><strong>{entry.order_id || "-"}</strong></td>
                        <td>{formatSettlementStatus(entry.previous_status)}</td>
                        <td>{formatSettlementStatus(entry.current_status)}</td>
                        <td>{formatCurrency(entry.previous_difference)}</td>
                        <td>{formatCurrency(entry.current_difference)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {uploadWorkflow && (() => {
        const report = reportTypes[uploadWorkflow.type];
        const previewRows = uploadWorkflow.rows.slice(0, 5);
        const headers = Object.keys(previewRows[0] || {});
        const result = uploadWorkflow.result || {};
        return <div className="reports-modal-overlay reports-workflow-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !uploadWorkflow.importing) setUploadWorkflow(null); }}>
          <section className="reports-workflow-modal" role="dialog" aria-modal="true" aria-label="Upload report">
            <header className="reports-workflow-head"><div><span>Report upload</span><h2>{uploadWorkflow.step === 4 ? "Import completed" : uploadWorkflow.step === 3 ? "Confirm import" : uploadWorkflow.step === 2 ? "Validate report" : "Upload Report"}</h2><p>{report.title}</p></div><button type="button" onClick={() => !uploadWorkflow.importing && setUploadWorkflow(null)} aria-label="Close"><X size={19} /></button></header>
            {uploadWorkflow.step < 4 && <div className="reports-workflow-steps">{["Select File", "Validate Report", "Import"].map((step, index) => <div className={uploadWorkflow.step >= index + 1 ? "active" : ""} key={step}><b>{index + 1}</b>{step}</div>)}</div>}
            {uploadWorkflow.step === 1 && <div className="reports-workflow-content">
              <label className="reports-workflow-drop" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); selectUploadFile(event.dataTransfer.files?.[0]); }}>
                <UploadCloud size={36} /><strong>{uploadWorkflow.file ? uploadWorkflow.file.name : "Drop your report here or browse files"}</strong><span>{uploadWorkflow.file ? `${(uploadWorkflow.file.size / 1024 / 1024).toFixed(2)} MB · ${uploadWorkflow.file.name.split(".").pop().toUpperCase()}` : "CSV or Excel · .csv, .xlsx, .xls"}</span>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={(event) => { selectUploadFile(event.target.files?.[0]); event.target.value = ""; }} />
                {uploadWorkflow.file && <button type="button" className="reports-btn secondary" onClick={(event) => { event.preventDefault(); event.stopPropagation(); setUploadWorkflow((current) => ({ ...current, file: null, rows: [], error: "" })); }}>Remove file</button>}
              </label>
              {uploadWorkflow.error && <p className="reports-workflow-error"><AlertTriangle size={16} />{uploadWorkflow.error}</p>}
              <footer><button type="button" className="reports-btn secondary" onClick={() => setUploadWorkflow(null)}>Cancel</button><button type="button" className="reports-btn primary" disabled={!uploadWorkflow.file} onClick={() => setUploadWorkflow((current) => ({ ...current, step: 2 }))}>Next <ChevronRight size={16} /></button></footer>
            </div>}
            {uploadWorkflow.step === 2 && <div className="reports-workflow-content">
              <div className="reports-workflow-file"><FileSpreadsheet size={21} /><div><strong>{uploadWorkflow.file.name}</strong><span>{uploadWorkflow.rows.length.toLocaleString()} data rows · Validation passed</span></div></div>
              <div className="reports-workflow-stats"><div><span>Total rows detected</span><strong>{uploadWorkflow.rows.length.toLocaleString()}</strong></div><div><span>Rows with required columns</span><strong>{uploadWorkflow.rows.length.toLocaleString()}</strong></div><div><span>Database duplicates</span><strong>Checked during import</strong></div><div><span>Row-level validation</span><strong>Applied during import</strong></div></div>
              <p className="reports-workflow-note">Required columns are present. Duplicate checks, database conflicts, order linking, and final counts are determined by the existing import service.</p>
              <div className="reports-preview-head"><strong>Data preview <small>(first {previewRows.length} rows)</small></strong></div>
              <div className="reports-preview-table"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{previewRows.map((row, index) => <tr key={index}>{headers.map((header) => <td key={header}>{String(row[header] ?? "")}</td>)}</tr>)}</tbody></table></div>
              {uploadWorkflow.error && <p className="reports-workflow-error"><AlertTriangle size={16} />{uploadWorkflow.error}</p>}
              <footer><button type="button" className="reports-btn secondary" onClick={() => setUploadWorkflow((current) => ({ ...current, step: 1, error: "" }))}>Back</button><button type="button" className="reports-btn primary" onClick={() => setUploadWorkflow((current) => ({ ...current, step: 3 }))}>Next <ChevronRight size={16} /></button></footer>
            </div>}
            {uploadWorkflow.step === 3 && <div className="reports-workflow-content">
              <div className="reports-workflow-warning"><AlertTriangle size={19} /><div><strong>Ready to import {uploadWorkflow.rows.length.toLocaleString()} rows</strong><span>The server will apply the existing duplicate and invalid row rules. No records have been imported yet.</span></div></div>
              <dl className="reports-workflow-details"><dt>Report type</dt><dd>{report.title}</dd><dt>File name</dt><dd>{uploadWorkflow.file.name}</dd><dt>Total rows</dt><dd>{uploadWorkflow.rows.length.toLocaleString()}</dd><dt>Import counts</dt><dd>Calculated by the server</dd></dl>
              {uploadWorkflow.error && <p className="reports-workflow-error"><AlertTriangle size={16} />{uploadWorkflow.error}</p>}
              <footer><button type="button" className="reports-btn secondary" disabled={uploadWorkflow.importing} onClick={() => setUploadWorkflow((current) => ({ ...current, step: 2, error: "" }))}>Back</button><button type="button" className="reports-btn secondary" disabled={uploadWorkflow.importing} onClick={() => setUploadWorkflow(null)}>Cancel</button><button type="button" className="reports-btn primary" disabled={uploadWorkflow.importing} onClick={confirmUpload}>{uploadWorkflow.importing ? "Importing…" : "Confirm Import"}</button></footer>
            </div>}
            {uploadWorkflow.step === 4 && <div className="reports-workflow-content reports-workflow-result"><CheckCircle2 size={48} /><h3>Report imported successfully</h3><p>Server confirmed processing for {uploadWorkflow.file.name}.</p><div className="reports-workflow-stats"><div><span>Total rows processed</span><strong>{result.totalRows ?? "—"}</strong></div><div><span>Successfully imported</span><strong>{result.imported ?? "—"}</strong></div><div><span>Duplicates skipped</span><strong>{result.duplicates ?? "—"}</strong></div><div><span>Successfully linked</span><strong>{result.linked ?? "—"}</strong></div></div><footer><button type="button" className="reports-btn primary" onClick={() => setUploadWorkflow(null)}>Close</button></footer></div>}
          </section>
        </div>;
      })()}

    </div>
  );
}

function UploadPanel({ title, description, mapping, summary, busy, onUpload, onSample }) {
  const hasErrors = summary.errors?.length > 0;

  return (
    <article className="reports-upload-card">
      <div className="reports-upload-head">
        <div className="reports-upload-icon">
          <UploadCloud size={22} />
        </div>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>

      <label className="reports-dropzone">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(event) => onUpload(event.target.files?.[0])}
          disabled={busy}
        />
        <span>{busy ? "Uploading..." : "Choose CSV or Excel file"}</span>
        <small>{summary.fileName || "No file selected"}</small>
      </label>

      <button type="button" className="reports-btn secondary reports-sample-btn" onClick={onSample}>
        <Download size={15} />
        Download Dummy
      </button>

      <div className="reports-mapping">
        {Object.entries(mapping).map(([target, source]) => (
          <div key={target}>
            <span>{target}</span>
            <strong>{source}</strong>
          </div>
        ))}
      </div>

      <div className={`reports-summary ${hasErrors ? "error" : ""}`}>
        {hasErrors ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
        <div>
          <strong>{hasErrors ? "Validation issue" : `${summary.imported || 0} rows imported`}</strong>
          <p>
            Total {summary.totalRows || 0} rows, {summary.duplicates || 0} duplicate/skipped, {summary.linked || 0} linked.
          </p>
          {hasErrors && summary.errors.map((error) => <p key={error}>{error}</p>)}
        </div>
      </div>
    </article>
  );
}

function ProductPicker({ products = [], value, onChange, placeholder = "Select product" }) {
  const selectedProduct = products.find((product) => String(product.id) === String(value));
  const [query, setQuery] = useState(selectedProduct?.name || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(selectedProduct?.name || "");
  }, [selectedProduct?.name]);

  const filteredProducts = useMemo(() => {
    const needle = normalizeKey(query);
    return products
      .filter((product) => {
        if (!needle) return true;
        return normalizeKey(`${product.name || ""} ${product.category || ""} ${product.id || ""}`).includes(needle);
      })
      .slice(0, 80);
  }, [products, query]);

  return (
    <div className="reports-product-picker">
      <input
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          onChange("");
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
      />
      {open && (
        <div className="reports-product-picker-menu">
          {filteredProducts.length === 0 ? (
            <button type="button" disabled>
              No product found
            </button>
          ) : (
            filteredProducts.map((product) => (
              <button
                type="button"
                key={product.id}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(String(product.id));
                  setQuery(product.name || `Product #${product.id}`);
                  setOpen(false);
                }}
              >
                <strong>{product.name || `Product #${product.id}`}</strong>
                <small>{product.category || `Product #${product.id}`}</small>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function DifferenceList({ rows, emptyText, renderRow }) {
  if (!rows.length) return <div className="reports-sku-empty">{emptyText}</div>;

  return (
    <div className="reports-difference-list">
      {rows.map((row, index) => (
        <div key={`${row.awb_number || row.tracking_id || row.product_name || "row"}-${index}`} className="reports-difference-row">
          {renderRow(row)}
        </div>
      ))}
    </div>
  );
}

function SelectFilter({ label, value, options, onChange }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="all">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}
